import React, { useState } from 'react';
import { MailIcon, BellIcon, SparklesIcon, AlertCircleIcon } from 'lucide-react';
import { motion } from 'framer-motion';
// ============================================================
// ACTIVECAMPAIGN CONFIGURATION
// Follow the setup instructions below to get these values
// from your ActiveCampaign account.
// ============================================================
const ACTIVECAMPAIGN_CONFIG = {
  // Your ActiveCampaign account name (the subdomain part)
  // Example: if your login URL is https://mycompany.activehosted.com
  // then your account name is "mycompany"
  ACCOUNT_NAME: 'seanacjackson0825',
  // Form ID — found in your ActiveCampaign form's embed code
  // Look for the "f" value in the form action URL
  FORM_ID: '3',
  // Form Action ID — found as the "u" value in the embed code
  FORM_ACTION_ID: '3',
  // Optional: List ID if you want to add contacts to a specific list
  // Leave as empty string if not needed
  LIST_ID: '5'
};
export function CountdownSignup() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus('submitting');
    setErrorMessage('');
    try {
      // Build the form data for ActiveCampaign
      const formData = new FormData();
      formData.append('u', ACTIVECAMPAIGN_CONFIG.FORM_ACTION_ID);
      formData.append('f', ACTIVECAMPAIGN_CONFIG.FORM_ID);
      formData.append('s', ''); // required empty field
      formData.append('c', '0'); // required
      formData.append('m', '0'); // required
      formData.append('act', 'sub'); // action: subscribe
      formData.append('v', '2'); // version
      formData.append('or', window.location.href); // origin URL
      formData.append('email', email);
      // If you have a list ID, include it
      if (ACTIVECAMPAIGN_CONFIG.LIST_ID) {
        formData.append('nlbox[]', ACTIVECAMPAIGN_CONFIG.LIST_ID);
      }
      // Submit to ActiveCampaign
      // Using no-cors mode because ActiveCampaign's form endpoint
      // doesn't support CORS headers. The request still goes through,
      // but we can't read the response.
      await fetch(`https://${ACTIVECAMPAIGN_CONFIG.ACCOUNT_NAME}.activehosted.com/proc.php`, {
        method: 'POST',
        body: formData,
        mode: 'no-cors'
      });
      // If we get here without a network error, the submission went through
      setStatus('success');
      setEmail('');
    } catch (error) {
      console.error('ActiveCampaign submission error:', error);
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again.');
    }
  };
  return <section id="signup" className="py-20 md:py-32 px-4 bg-pulse-navy relative overflow-hidden" data-id="element-71">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-20" data-id="element-72">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-pulse-blue rounded-full blur-3xl" data-id="element-73" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pulse-orange rounded-full blur-3xl" data-id="element-74" />
      </div>

      <div className="max-w-4xl mx-auto relative z-10" data-id="element-75">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-8 md:p-12 text-center shadow-2xl" data-id="element-76">
          <motion.div initial={{
          scale: 0
        }} whileInView={{
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          type: 'spring',
          delay: 0.2
        }} className="w-20 h-20 mx-auto bg-gradient-to-br from-pulse-orange to-pulse-yellow rounded-2xl flex items-center justify-center mb-8 shadow-lg shadow-pulse-orange/20 rotate-12" data-id="element-77">
            <BellIcon className="h-10 w-10 text-white -rotate-12" data-id="element-78" />
          </motion.div>

          <h2 className="text-4xl md:text-5xl font-black text-white uppercase tracking-tight mb-4" data-id="element-79">
            Be The First To Know
          </h2>

          <p className="text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto" data-id="element-80">
            Join our VIP waitlist to get exclusive updates on our grand opening,
            early access to party bookings, and special pre-launch offers!
          </p>

          {status === 'success' ? <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="bg-green-500/20 border border-green-500/50 text-green-100 rounded-2xl p-6 flex items-center justify-center gap-3" data-id="element-81">
              <SparklesIcon className="h-6 w-6 text-green-400" data-id="element-82" />
              <span className="font-bold text-lg" data-id="element-83">
                You're on the list! Keep an eye on your inbox.
              </span>
            </motion.div> : <form onSubmit={handleSubmit} className="max-w-md mx-auto relative" data-id="element-84">
              <div className="relative flex items-center" data-id="element-85">
                <MailIcon className="absolute left-4 h-6 w-6 text-white/40" data-id="element-86" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter your email address" required disabled={status === 'submitting'} className="w-full bg-white/5 border-2 border-white/20 rounded-full py-4 pl-14 pr-36 text-white placeholder:text-white/40 focus:outline-none focus:border-pulse-blue focus:bg-white/10 transition-all disabled:opacity-50" data-id="element-87" />
                <button type="submit" disabled={status === 'submitting'} className="absolute right-2 top-2 bottom-2 bg-pulse-blue hover:bg-sky-400 text-pulse-navy font-bold uppercase tracking-wide px-6 rounded-full transition-colors disabled:opacity-70 flex items-center justify-center min-w-[120px]" data-id="element-88">
                  {status === 'submitting' ? <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 1,
                repeat: Infinity,
                ease: 'linear'
              }} className="w-5 h-5 border-2 border-pulse-navy border-t-transparent rounded-full" data-id="element-89" /> : 'Notify Me'}
                </button>
              </div>

              {status === 'error' && <motion.p initial={{
            opacity: 0,
            y: -5
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-red-400 text-sm mt-3 flex items-center justify-center gap-2" data-id="element-90">
                  <AlertCircleIcon className="h-4 w-4" data-id="element-91" />
                  {errorMessage}
                </motion.p>}

              <p className="text-white/40 text-sm mt-4" data-id="element-92">
                We respect your privacy. No spam, ever.
              </p>
            </form>}
        </motion.div>
      </div>
    </section>;
}